/*
=========================================================
* FMP Soluções — Produtos / Licenças / RevBits
* Rota: /pages/produtos/licencas/revbits
=========================================================
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Divider from "@mui/material/Divider";
import { Link as RouterLink } from "react-router-dom";

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";
import MKBadge from "components/MKBadge";

// Navbar / Footer
import DefaultNavbar from "examples/Navbars/DefaultNavbar";
import FMPFooter from "examples/Footers/FMPFooter";

// Routes
import routes from "routes";
import footerRoutes from "footer.routes";

// Image
import bgImage from "assets/images/produtos.jpg";

// PropTypes
import PropTypes from "prop-types";

// ─── Dados dos 6 módulos ────────────────────────────────────────────────────

const modules = [
  {
    icon: "psychology",
    name: "Cyber Intelligence Platform",
    description:
      "Plataforma central de inteligência de ameaças — agrega, correlaciona e prioriza dados de segurança de toda a organização em um painel unificado, com visibilidade em tempo real.",
    highlights: ["Correlação de ameaças", "Dashboard unificado", "Inteligência em tempo real"],
  },
  {
    icon: "pest_control",
    name: "Deception Technology",
    description:
      "Cria armadilhas digitais (honeypots e honeytokens) para detectar invasores que já estão dentro da rede — capturando ataques antes que causem danos reais.",
    highlights: ["Honeypots e honeytokens", "Detecção de movimento lateral", "Alertas em tempo real"],
  },
  {
    icon: "mark_email_unread",
    name: "Email Security",
    description:
      "Proteção avançada contra phishing, spear phishing, BEC e malware em anexos — com análise de links em tempo real e sandboxing de arquivos suspeitos.",
    highlights: ["Anti-phishing e BEC", "Sandboxing de anexos", "Análise de links em tempo real"],
  },
  {
    icon: "laptop_mac",
    name: "Endpoint Security",
    description:
      "EDR de próxima geração com detecção comportamental, resposta automatizada e isolamento de endpoints comprometidos — sem depender de assinaturas.",
    highlights: ["Detecção comportamental", "Resposta automatizada", "Isolamento de endpoints"],
  },
  {
    icon: "admin_panel_settings",
    name: "Privileged Access Management",
    description:
      "Controle granular de acessos privilegiados — gravação de sessões, rotação automática de credenciais e aprovação por fluxo de trabalho para contas administrativas.",
    highlights: ["Gravação de sessões", "Rotação de credenciais", "Aprovação por workflow"],
  },
  {
    icon: "lan",
    name: "Zero Trust Network",
    description:
      "Implementação de arquitetura Zero Trust — microsegmentação, verificação contínua de identidade e acesso mínimo necessário para cada recurso da rede.",
    highlights: ["Microsegmentação", "Verificação contínua", "Acesso mínimo necessário"],
  },
];

// ─── Componentes ────────────────────────────────────────────────────────────

function ModuleCard({ mod }) {
  return (
    <MKBox
      borderRadius="lg"
      shadow="md"
      p={3}
      height="100%"
      display="flex"
      flexDirection="column"
      sx={{
        backgroundColor: ({ palette: { white } }) => white.main,
        border: ({ borders: { borderWidth, borderColor } }) =>
          `${borderWidth[1]} solid ${borderColor}`,
        transition: "box-shadow 200ms ease",
        "&:hover": { boxShadow: ({ boxShadows: { lg } }) => lg },
      }}
    >
      <MKBox
        width="3rem"
        height="3rem"
        variant="gradient"
        bgColor="info"
        color="white"
        coloredShadow="info"
        display="flex"
        alignItems="center"
        justifyContent="center"
        borderRadius="xl"
        mb={2}
      >
        <Icon fontSize="small">{mod.icon}</Icon>
      </MKBox>

      <MKTypography variant="h6" mb={1}>
        {mod.name}
      </MKTypography>
      <MKTypography variant="body2" color="text" mb={2} sx={{ flexGrow: 1 }}>
        {mod.description}
      </MKTypography>

      <MKBox>
        {mod.highlights.map((h, i) => (
          <MKBox key={i} display="flex" alignItems="center" gap={1} mb={0.5}>
            <Icon fontSize="small" color="info" sx={{ fontSize: "1rem" }}>check_circle_outline</Icon>
            <MKTypography variant="caption" color="text">{h}</MKTypography>
          </MKBox>
        ))}
      </MKBox>
    </MKBox>
  );
}

ModuleCard.propTypes = {
  mod: PropTypes.shape({
    icon: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    highlights: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
};

// ─── Página ─────────────────────────────────────────────────────────────────

function RevBits() {
  return (
    <>
      <DefaultNavbar
        routes={routes}
        action={{
          type: "internal",
          route: "/pages/ajuda/contate-nos",
          label: "Contate-nos",
          color: "info",
        }}
        sticky
      />

      {/* Hero */}
      <MKBox
        minHeight="50vh"
        width="100%"
        sx={{
          backgroundImage: ({ functions: { linearGradient, rgba }, palette: { gradients } }) =>
            `${linearGradient(
              rgba(gradients.dark.main, 0.65),
              rgba(gradients.dark.state, 0.65)
            )}, url(${bgImage})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "grid",
          placeItems: "center",
        }}
      >
        <Container>
          <Grid container item xs={12} md={8} justifyContent="center" mx="auto" textAlign="center">
            <MKTypography variant="h1" color="white" mb={1}>
              RevBits
            </MKTypography>
            <MKTypography variant="h5" color="white" opacity={0.8} mb={3}>
              Plataforma unificada de cibersegurança com seis módulos integrados —
              do controle de acesso privilegiado à proteção de email, em uma única solução.
            </MKTypography>
            <MKButton
              variant="gradient"
              color="info"
              size="large"
              component="a"
              href="/pages/ajuda/contate-nos"
            >
              Solicitar demonstração
            </MKButton>
          </Grid>
        </Container>
      </MKBox>

      {/* Conteúdo */}
      <Card
        sx={{
          p: 2,
          mx: { xs: 2, lg: 3 },
          mt: -8,
          mb: 4,
          backgroundColor: ({ palette: { white }, functions: { rgba } }) => rgba(white.main, 0.8),
          backdropFilter: "saturate(200%) blur(30px)",
          boxShadow: ({ boxShadows: { xxl } }) => xxl,
        }}
      >
        <MKBox component="section" py={{ xs: 6, md: 10 }}>
          <Container>

            {/* Intro */}
            <Grid container justifyContent="center" mb={6}>
              <Grid item xs={12} md={8} textAlign="center">
                <MKTypography variant="h3" mb={1}>
                  Uma plataforma. Seis módulos. Proteção completa.
                </MKTypography>
                <MKTypography variant="body2" color="text">
                  A RevBits oferece uma abordagem integrada à cibersegurança — cada módulo opera
                  de forma independente ou em conjunto com os demais, compartilhando inteligência
                  de ameaças em tempo real. A FMP Soluções é distribuidora exclusiva para toda a
                  América do Sul, com suporte local, integração especializada e licenciamento
                  adaptado ao mercado brasileiro.
                </MKTypography>
              </Grid>
            </Grid>

            {/* Grid de módulos */}
            <Grid container spacing={3} mb={8}>
              {modules.map((mod, i) => (
                <Grid item xs={12} sm={6} lg={4} key={i}>
                  <ModuleCard mod={mod} />
                </Grid>
              ))}
            </Grid>

            <Divider sx={{ mb: 6 }} />

            {/* Por que RevBits via FMP */}
            <Grid container spacing={4} alignItems="center" mb={6}>
              <Grid item xs={12} md={5}>
                <MKTypography variant="h4" mb={2}>
                  Por que adquirir via FMP Soluções?
                </MKTypography>
                {[
                  { icon: "public", text: "Distribuidor exclusivo para toda a América do Sul — contrato VAD assinado com a RevBits LLC (Nova York)" },
                  { icon: "support_agent", text: "Suporte nível 1 e 2 em português, com equipe técnica especializada em segurança cibernética" },
                  { icon: "gavel", text: "Licenciamento com conformidade à LGPD e legislação brasileira" },
                  { icon: "integration_instructions", text: "Integração com sua infraestrutura existente — sistemas legados, SIEM, firewalls e endpoints" },
                  { icon: "school", text: "Treinamento e capacitação da sua equipe via Academia Ciber da FMP" },
                ].map((item, i) => (
                  <MKBox key={i} display="flex" alignItems="flex-start" gap={2} mb={2}>
                    <MKBox
                      width="2.5rem"
                      height="2.5rem"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      borderRadius="lg"
                      sx={{ backgroundColor: ({ palette: { info } }) => `${info.main}18`, flexShrink: 0 }}
                    >
                      <Icon color="info">{item.icon}</Icon>
                    </MKBox>
                    <MKTypography variant="body2" color="text" sx={{ pt: 0.5 }}>
                      {item.text}
                    </MKTypography>
                  </MKBox>
                ))}
              </Grid>

              {/* CTA lateral */}
              <Grid item xs={12} md={6} sx={{ ml: "auto" }}>
                <MKBox
                  borderRadius="xl"
                  p={4}
                  textAlign="center"
                  sx={{
                    background: ({ palette: { gradients } }) =>
                      `linear-gradient(195deg, ${gradients.dark.main}, ${gradients.dark.state})`,
                  }}
                >
                  <MKTypography variant="h4" color="white" mb={1}>
                    Pronto para uma demonstração?
                  </MKTypography>
                  <MKTypography variant="body2" color="white" opacity={0.8} mb={3}>
                    Fale com um especialista da FMP Soluções para entender qual combinação de
                    módulos faz sentido para a sua organização e receber uma proposta comercial.
                  </MKTypography>
                  <MKButton
                    variant="gradient"
                    color="info"
                    size="large"
                    component="a"
                    href="/pages/ajuda/contate-nos"
                    fullWidth
                    sx={{ mb: 2 }}
                  >
                    Solicitar demonstração
                  </MKButton>
                  <MKButton
                    variant="text"
                    color="white"
                    component={RouterLink}
                    to="/pages/produtos/licencas"
                    fullWidth
                  >
                    ← Voltar para Licenças
                  </MKButton>
                </MKBox>
              </Grid>
            </Grid>

          </Container>
        </MKBox>
      </Card>

      <MKBox pt={6} px={1} mt={6}>
        <FMPFooter content={footerRoutes} />
      </MKBox>
    </>
  );
}

export default RevBits;
